import type { StoreActionApi } from 'react-sweet-state';
import type { MessageDescriptor } from 'react-intl-next';

import type { ADFEntity } from '@atlaskit/adf-utils/types';

import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import type { CreateSnippetPayload } from '../../services/blockServiceTypes.ts';
import { isBodyTooLargeError } from '../../services/blockServiceTypes.ts';
import { messages as baseMessages } from '../../modals/SnippetBaseModal.i18n.ts';
import { addSnippetQuickInsertItem } from '../../quick-insert/snippetQuickInsertItemApi.tsx';
import { SnippetBodyFormat, SnippetProduct } from '../../snippetTypes.ts';
import type { Snippet } from '../../snippetTypes.ts';
import type { SnippetsState } from '../snippetsStore.types.ts';

import { shareActions, type ShareSnippetOptions } from './shareActions.ts';

/**
 * Returns the appropriate error flag title and description MessageDescriptors based on whether
 * the error is a body-too-large error. Uses shared base messages for too-large errors and
 * falls back to the provided action-specific messages otherwise.
 */
export const getSnippetErrorFlagMessages = (
	error: Error,
	actionErrorFlagTitle: MessageDescriptor,
	actionErrorFlagDescription: MessageDescriptor,
): { flagTitle: MessageDescriptor; flagDescription: MessageDescriptor } => {
	const isBodyTooLarge = isBodyTooLargeError(error);
	return {
		flagTitle: isBodyTooLarge ? baseMessages.contentTooLargeFlagTitle : actionErrorFlagTitle,
		flagDescription: isBodyTooLarge
			? baseMessages.contentTooLargeFlagDescription
			: actionErrorFlagDescription,
	};
};

/**
 * Shows a success flag with the given title and description.
 */
export const showSnippetSuccessFlag = (
	flags: WithFlagsProps['flags'],
	title: string,
	description?: string,
): void => {
	if (!flags) return;
	flags.showSuccessFlag({
		title,
		description: description ?? '',
		isAutoDismiss: true,
	});
};

/**
 * Shows an error flag with the given title and description.
 */
export const showSnippetErrorFlag = (
	flags: WithFlagsProps['flags'],
	title: string,
	description?: string,
): void => {
	if (!flags) return;
	flags.showErrorFlag({
		title,
		description: description ?? '',
		isAutoDismiss: true,
	});
};

/**
 * Builds a CreateSnippetPayload for the create snippet API from name, ADF body, and optional description.
 */
export function buildCreatePayload(
	name: string,
	body: ADFEntity,
	description?: string,
): CreateSnippetPayload {
	const descriptionValue = description?.trim();
	return {
		name,
		...(descriptionValue && { description: descriptionValue }),
		body: JSON.stringify(body),
		bodyFormat: SnippetBodyFormat.ADF,
		product: SnippetProduct.CONFLUENCE,
	};
}

export type AddSnippetToQuickInsertAndShareOptions = Pick<
	ShareSnippetOptions,
	'flags' | 'intl' | 'editorAPI'
> & {
	isShared?: boolean;
};

export type AddSnippetToQuickInsertAndShareParams = {
	snippet: Snippet;
	options: AddSnippetToQuickInsertAndShareOptions;
	storeApi: StoreActionApi<SnippetsState>;
	onShareFailure?: (snippetId: string, error: Error) => void;
};

/**
 * Adds snippet to quick insert when editorAPI is set, and shares the snippet when site sharing
 * is enabled and isShared is true. Call after create or duplicate succeeds.
 */
export async function addSnippetToQuickInsertAndShare({
	snippet,
	options,
	storeApi,
	onShareFailure,
}: AddSnippetToQuickInsertAndShareParams): Promise<void> {
	const { getState, dispatch } = storeApi;
	const { flags, intl, editorAPI, isShared } = options;
	if (editorAPI) {
		addSnippetQuickInsertItem({ snippet, editorAPI });
	}
	if (getState().isSnippetSharingForSiteEnabled && isShared) {
		await dispatch(
			shareActions.shareSnippet(snippet.snippetId, {
				flags,
				intl,
				editorAPI: editorAPI ?? undefined,
				onShareFailure,
			}),
		);
	}
}
